import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollText } from 'lucide-react';

interface SystemLog {
  id: string;
  level: string;
  message: string;
  category: string;
  timestamp: number;
  metadata?: any;
}

function SystemLogs() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/logs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch system logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Insights"
        icon={ScrollText}
        title={t('systemLogs.title')}
        subtitle={t('systemLogs.subtitle')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('systemLogs.recentLogs')}</CardTitle>
          <CardDescription>
            {t('systemLogs.description', { count: logs.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('systemLogs.table.time')}</TableHead>
                <TableHead>{t('systemLogs.table.level')}</TableHead>
                <TableHead>{t('systemLogs.table.category')}</TableHead>
                <TableHead>{t('systemLogs.table.message')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="empty-state text-muted-foreground">
                    {t('dashboard.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-mono text-xs">
                      {formatDateTime(log.timestamp)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={log.level} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-primary/20 bg-primary/10 text-primary">
                        {log.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-2xl break-words">{log.message}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default SystemLogs;
